import { useEffect, useState } from 'react';
import './CameraHud.css';
import { OVERLAY_LABELS } from '../../config/jeff.ts';

/**
 * The camera's own on-screen display, in the corner of the shot.
 *
 * This started as a dark scrim over the scene, then moved to a caption plate
 * beneath the frame — the right call when the pen was a black shed, and wrong
 * once it became a sunlit paddock, because the scrim turned into a grey smear
 * across the sky. It is back inside the picture now, which works because it is no
 * longer a panel: just text with a hard shadow, the way a real camera burns its
 * timecode into the signal. Nothing is drawn behind it, so there is nothing to
 * smear.
 *
 * It is also much less than it was. The rolling analyst commentary went with the
 * move — a security camera does not narrate, and a caption reading CONSULTING
 * MACRO CONDITIONS was the joke being explained rather than told. What is left is
 * what a camera actually shows: that it is recording, which camera it is, and the
 * time.
 */
export function CameraHud() {
  return (
    <div className="cam" aria-hidden="true">
      <span className="cam__live">
        <span className="cam__dot" />
        {OVERLAY_LABELS.live}
      </span>
      <span className="cam__slug">{OVERLAY_LABELS.camera}</span>
      <Timecode />
    </div>
  );
}

/**
 * A wall-clock timecode.
 *
 * Decorative, and the whole HUD is hidden from assistive technology: a screen
 * reader gains nothing from a clock ticking once a second, and the result is
 * announced properly elsewhere.
 */
function Timecode() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <span className="cam__time">
      {pad(now.getDate())}.{pad(now.getMonth() + 1)}.{now.getFullYear()}
      {'  '}
      {pad(now.getHours())}:{pad(now.getMinutes())}:{pad(now.getSeconds())}
    </span>
  );
}
